/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { H3Event } from 'h3'
import type { FetchContext, FetchError, FetchHook } from 'ofetch'

import { trace } from '@backmarket/instrument/trace'
import { type SafeResult, tryCatch } from '@backmarket/utils/function/tryCatch'
import { merge } from '@backmarket/utils/object/merge'
import { omit } from '@backmarket/utils/object/omit'
import { resolvePath } from '@backmarket/utils/url/resolvePath'

import { BOT_CHALLENGE, UNAUTHENTICATED_CLIENT_FETCH_EVENT } from '../constants'
import type { ApiSpecs } from '../specs'

import { getRetryReason } from './getRetryReason'
import { type ErrorPayloadNormalized, fromAnyError } from './normalizeError'

type BaseRequestOptions = {
  body?:
    | Record<string, unknown>
    | FormData
    | URLSearchParams
    | Array<any>
    | undefined
  query?: Record<string, unknown> | undefined
}

type FilterOutUndefinedOptions<Opts> = {
  [K in keyof Opts as Opts[K] extends undefined ? never : K]: Opts[K]
}

/**
 * SmartOmit omits the keys like Omit, but does not break when the generic is a union between undefined an a Record
 */
type SmartOmit<T, K extends string | number> = T extends undefined
  ? T
  : Omit<T, K>

export type HttpFetchOptionsWithoutMethod<
  Path extends string = string,
  RequestOptions extends BaseRequestOptions = BaseRequestOptions,
> = SmartOmit<
  Parameters<typeof $fetch>[1],
  'body' | 'query' | 'pathParams' | 'method' | 'headers'
> &
  RequestOptions &
  PathParams<Path> & {
    headers?: HeadersInit | Record<string, string | undefined>
  }
export type HttpFetchOptions<
  Method extends MethodType = 'GET',
  Path extends string = string,
  RequestOptions extends BaseRequestOptions = BaseRequestOptions,
> = SmartOmit<Parameters<typeof $fetch>[1], 'body' | 'query' | 'pathParams'> & {
  method?: Method
} & RequestOptions &
  PathParams<Path>

/**
 * Extracts all the segments of a path
 * "/users/:userId/orders/:orderId" => "users" | ":userId" | "orders" | ":orderId"
 */
type PathSegments<Path extends string> =
  Path extends `/${infer A extends string}/${infer Rest extends string}`
    ? A | PathSegments<Rest>
    : Path extends `${infer A extends string}/${infer Rest extends string}`
      ? A | PathSegments<Rest>
      : Path

/**
 * Filters out the non-dynamic parameters. Only keeps those prefixed with ":"
 * "users" | ":userId" | "orders" | ":orderId" => "userId" | "orderId"
 */
type OnlyDynamicParams<Segment extends string> =
  Segment extends `:${infer Param}` ? Param : never

/**
 * Extracts the dynamic parameters from a path
 * Example: "/users/:userId/orders/:orderId" => "userId" | "orderId"
 */
export type DynamicParams<Path extends string> = OnlyDynamicParams<
  PathSegments<Path>
>

type RequiresPathParams<Path extends string> =
  DynamicParams<Path> extends never ? false : true

type PathParams<Path extends string> =
  DynamicParams<Path> extends never
    ? {
        pathParams?: never
      }
    : { pathParams: Record<DynamicParams<Path>, string | number> }

export type MissingRequiredPathParams =
  'Error: Missing required pathParams in $httpFetch. Your path contains path params. Provide them in the second argument of $httpFetch as { pathParams }'

type URL = string

export type EndpointDefinition<
  Response = unknown,
  RequestOptions extends BaseRequestOptions = BaseRequestOptions,
> = { response: Response } & RequestOptions

export type MethodType = 'POST' | 'PATCH' | 'DELETE' | 'GET' | 'PUT' | 'OPTIONS'

type EndpointObject = Partial<Record<MethodType, EndpointDefinition>>

export type EndpointResponse<
  Specs,
  Path extends keyof Specs,
  Method extends MethodType,
> =
  Specs[Path] extends Record<Method, EndpointDefinition>
    ? Specs[Path][Method] extends EndpointDefinition<infer Response>
      ? Response
      : never
    : never

// eslint-disable-next-line @typescript-eslint/no-empty-interface
type BaseApiSpecs = Record<URL, EndpointObject>

// Get Nuxt's InternalApi type
type InternalApi = import('nitropack/types').InternalApi

// Convert Nuxt's InternalApi format to our EndpointObject format
type ConvertNuxtApi<T> = {
  [K in keyof T]: {
    [M in keyof T[K] as M extends 'default' ? 'GET' : Uppercase<string & M>]: {
      response: T[K][M]
    }
  }
}

// Combine ApiSpecs with Nuxt's InternalApi
type CombinedApiSpecs = ApiSpecs & ConvertNuxtApi<InternalApi>

interface HttpFetchTryCatch {
  tryCatch<
    Path extends keyof CombinedApiSpecs & string,
    Method extends MethodType = 'GET',
  >(
    request: Path,
    requestOptions: Method extends 'GET'
      ? HttpFetchOptionsWithoutMethod<
          Path,
          CombinedApiSpecs[Path] extends Record<'GET', EndpointDefinition>
            ? CombinedApiSpecs[Path]['GET'] extends EndpointDefinition<
                any,
                infer ReqOpts
              >
              ? FilterOutUndefinedOptions<Omit<ReqOpts, 'response'>>
              : never
            : never
        > & {
          method?: 'GET'
        }
      : HttpFetchOptions<
          Method,
          Path,
          CombinedApiSpecs[Path] extends Record<Method, EndpointDefinition>
            ? CombinedApiSpecs[Path][Method] extends EndpointDefinition<
                any,
                infer ReqOpts
              >
              ? FilterOutUndefinedOptions<Omit<ReqOpts, 'response'>>
              : never
            : never
        >,
  ): Promise<
    Method extends 'GET'
      ? SafeResult<EndpointResponse<CombinedApiSpecs, Path, 'GET'>, FetchError>
      : SafeResult<EndpointResponse<CombinedApiSpecs, Path, Method>, FetchError>
  >
  // Path only overload
  tryCatch<
    Path extends keyof CombinedApiSpecs & string,
    Method extends MethodType,
  >(
    request: Path,
  ): RequiresPathParams<Path> extends true
    ? Promise<MissingRequiredPathParams>
    : CombinedApiSpecs[Path] extends Record<'GET', EndpointDefinition>
      ? Promise<SafeResult<CombinedApiSpecs[Path]['GET']['response']>>
      : never
}
export interface HttpFetch extends HttpFetchTryCatch {
  // Single overload that handles all cases
  <
    Path extends keyof CombinedApiSpecs & string,
    Method extends MethodType = 'GET',
  >(
    request: Path,
    requestOptions: Method extends 'GET'
      ? HttpFetchOptionsWithoutMethod<
          Path,
          CombinedApiSpecs[Path] extends Record<'GET', EndpointDefinition>
            ? CombinedApiSpecs[Path]['GET'] extends EndpointDefinition<
                any,
                infer ReqOpts
              >
              ? FilterOutUndefinedOptions<Omit<ReqOpts, 'response'>>
              : never
            : never
        > & {
          method?: 'GET'
        }
      : HttpFetchOptions<
          Method,
          Path,
          CombinedApiSpecs[Path] extends Record<Method, EndpointDefinition>
            ? CombinedApiSpecs[Path][Method] extends EndpointDefinition<
                any,
                infer ReqOpts
              >
              ? FilterOutUndefinedOptions<Omit<ReqOpts, 'response'>>
              : never
            : never
        >,
  ): Promise<
    Method extends 'GET'
      ? EndpointResponse<CombinedApiSpecs, Path, 'GET'>
      : EndpointResponse<CombinedApiSpecs, Path, Method>
  >

  // Path only overload
  <Path extends keyof CombinedApiSpecs & string>(
    request: Path,
  ): RequiresPathParams<Path> extends true
    ? Promise<MissingRequiredPathParams>
    : CombinedApiSpecs[Path] extends Record<'GET', EndpointDefinition>
      ? Promise<CombinedApiSpecs[Path]['GET']['response']>
      : never

  create: <S extends undefined | BaseApiSpecs = undefined>(
    options: Partial<CreateHttpFetchOptions>,
  ) => HttpFetch
}

type CreateHttpFetchOptions = {
  /**
   * A reference to the current event being handled by the server.
   */
  event?: H3Event

  /**
   * Returns the default base URL to use for every request. It may be overridden
   * when actually firing the HTTP request via the `baseURL` request option.
   *
   * @example
   * createHttpFetch({
   *   getDefaultBaseUrl() {
   *     return 'https://base-url.com'
   *   },
   * })
   */
  getDefaultBaseUrl?: () => string

  /**
   * Returns the default headers to use for every request. Again, those headers
   * may be tweaked when actually firing the HTTP request via the `headers`
   * option. Note that both headers dictionary will be merged together.
   *
   * @example
   * createHttpFetch({
   *   getDefaultHeaders() {
   *     return { source: 'test' }
   *   },
   * })
   */
  getDefaultHeaders?: (path?: string) => Record<string, string>
}

const EVENT_LISTENERS = [
  'onRequest',
  'onRequestError',
  'onResponse',
  'onResponseError',
] as const

/**
 * Create a new `$httpFetch` instance tailored to specific needs. The returned
 * function is configured via the options passed as single parameter. Note that
 * all default options may be overridden when actually executing `$httpFetch`.
 *
 * We're using getters to retrieve the default options instead of raw values
 * because we want options to be read when the HTTP request is actually fired,
 * not when the `$httpFetch` instance is created. Values may change during
 * that time, and we want to rely on the most fresh values as possible.
 *
 * @example
 * const $httpFetch = createHttpFetch({
 *   getDefaultBaseUrl() {
 *     return 'https://base-url.com'
 *   },
 *   getDefaultHeaders() {
 *     return { source: 'test' }
 *   },
 * })
 *
 * const data = await $httpFetch('/endpoint', options)
 *
 * @warning
 * You most likely won't need to call this function yourself though. You are
 * probably interested in using the instance available on the global scope.
 */
export function createHttpFetch(
  globalOptions?: CreateHttpFetchOptions,
): HttpFetch {
  /**
   * Handle bot challenge errors returned from BOPIP.
   */
  function handleBotChallenge(error: ErrorPayloadNormalized): void {
    // We only want to handle bot challenges errors, we ignore other errors.
    if (error.type !== BOT_CHALLENGE.TYPE) return

    // Such errors should not happen on the server side. If they do, it means
    // that BOPIP is likely not behaving as expected.
    if (globalOptions?.event) {
      throw new Error('Bot challenge unexpectedly received server-side')
    }

    // If the error is a bot challenge, we redirect the user to the challenge
    // page where they can solve the challenge and prove they are not a bot.
    // We add a 'next' query parameter with the current URL so the user can be
    // redirected back after completing the challenge.
    window.location.assign(
      `${BOT_CHALLENGE.REDIRECT}?next=${encodeURIComponent(window.location.href)}`,
    )
  }

  /**
   * Handle 401 responses (unauthenticated).
   * Emits a custom event to notify the application of session expiration.
   */
  function handleUnauthenticated(statusCode: number): void {
    // Only handle 401 responses
    if (statusCode !== 401) return

    // Only handle on client side
    if (globalOptions?.event) return

    // Emit custom event that can be listened to by applications
    window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_CLIENT_FETCH_EVENT))
  }

  /**
   * Fire an HTTP request, as `$fetch` would do. Most of the function signature
   * is identical to what Nuxt provides out-of-the-box, so you may refer to
   * their documentation to learn more.
   *
   * @see https://nuxt.com/docs/api/utils/dollarfetch
   *
   * One main difference is that our custom `$httpFetch` function is able to
   * compute dynamic paths (i.e. paths including dynamic parameters). It works
   * pretty similarly to how you would expect it to work with Vue Router for
   * example. The following example would fire a request to `/users/123`.
   *
   * @example
   * $httpFetch('/users/:userId', {
   *   pathParams: {
   *     userId: 123,
   *   },
   * })
   */
  function $httpFetch<
    Path extends keyof CombinedApiSpecs & string,
    Method extends MethodType,
  >(
    request: Path,
    requestOptions?: CombinedApiSpecs[Path] extends Record<
      Method,
      EndpointDefinition
    >
      ? HttpFetchOptions<
          Method,
          Path,
          CombinedApiSpecs[Path][Method] extends EndpointDefinition<
            any,
            infer RequestOptions
          >
            ? FilterOutUndefinedOptions<Omit<RequestOptions, 'response'>>
            : never
        >
      : never,
  ): Promise<EndpointResponse<CombinedApiSpecs, Path, Method>> {
    /**
     * Simple wrapper around the `$fetch` function to make it easier to call
     * and to avoid repeating the same types over and over again.
     *
     * 1. We cast the return value to `Promise<ResponseBody>` because the
     * typechecking step of _other_ Nuxt modules would fail otherwise. I don't
     * know why, but it seems that the types are not correctly propagated.
     *
     * 2. We also pass `ResponseBody` as a type parameter to `$fetch` to avoid
     * another Nuxt-related issue. When the type is not explicitly provided,
     * the typechecking step crashes because of "Excessive stack depth".
     * See: https://github.com/unjs/nitro/issues/470
     */
    function callFetch(path: string, options: HttpFetchOptions) {
      return $fetch<EndpointResponse<CombinedApiSpecs, Path, Method>>(
        path,
        options,
      ) as Promise<EndpointResponse<CombinedApiSpecs, Path, Method>>
    }

    /**
     * From https://github.com/unjs/ofetch/blob/b1e49a049068ed81d741ce9ab45acbcd465b1f16/src/utils.ts#L129C1-L142C2
     */
    async function callHooks<C extends FetchContext = FetchContext>(
      context: C,
      hooks: FetchHook<C> | FetchHook<C>[] | undefined,
    ): Promise<void> {
      if (hooks) {
        if (Array.isArray(hooks)) {
          for (const hook of hooks) {
            // eslint-disable-next-line no-await-in-loop
            await hook(context)
          }
        } else {
          await hooks(context)
        }
      }
    }

    const defaultOptions: HttpFetchOptions = {
      baseURL: globalOptions?.getDefaultBaseUrl?.(),
      headers: globalOptions?.getDefaultHeaders?.(request),

      // Make sure that cookies are sent with the request. This is obviously
      // important because this is how we authenticate users on the API.
      credentials: 'include',

      method: 'GET',
      // Apply the custom event listeners to the request.
      onRequest: requestOptions?.onRequest,
      onRequestError: requestOptions?.onRequestError,
      onResponseError: requestOptions?.onResponseError,

      onResponse(context) {
        const { response } = context

        if (!response.ok) {
          // We have no other choice than using `_data` to access the content
          // of the response. That's the way `ofetch` works, and we can't do
          // anything about it.
          //
          // Note that by creating an intermediate error variable, we can
          // benefit from TypeScript type inference to get the correct type.
          // If we were to store the return value of `fromAnyError` directly
          // on `response._data`, the type would still be `any`.
          //
          // eslint-disable-next-line no-underscore-dangle
          const error = fromAnyError(response._data)

          // Also, it's expected to reassign the response `_data` property. It
          // is to mutate the response body with its normalized version in order
          // to access it everywhere else (in other `ofetch` listeners or after
          // the error is thrown).
          // eslint-disable-next-line no-param-reassign, no-underscore-dangle
          response._data = error

          handleUnauthenticated(response.status)
          handleBotChallenge(error)
        }

        return callHooks(context, requestOptions?.onResponse)
      },
    }

    // Merge everything into an empty object because the `merge` function
    // mutates the destination, and we do not want that. By using an empty
    // object as first parameter, we basically make it immutable.
    const requestOptionsButListeners = omit(requestOptions, EVENT_LISTENERS)
    const options = merge({}, defaultOptions, requestOptionsButListeners)

    /* Sort query keys alphabetically so the URL always matches mock interceptors
       regardless of the insertion order in the caller's query object. */
    if (options.query && typeof options.query === 'object') {
      options.query = Object.fromEntries(
        Object.entries(options.query as Record<string, unknown>).sort(
          ([a], [b]) => a.localeCompare(b),
        ),
      )
    }

    const resolvedPath = resolvePath(request, options.pathParams)

    // If there is no event, it means that were executing the code in the
    // browser. Since the `trace` function must only be called on the
    // server, we just fire the HTTP request with no tracing.
    // Note: Retry metrics are only tracked server-side, not in the browser.
    if (globalOptions?.event === undefined) {
      return callFetch(resolvedPath, options)
    }

    // For unknown reason this custom trace is needed to properly
    // create a nested relationship between the incoming http request
    // (http.server.request) and the outgoing request (http.client.request).
    // Without us creating a new span here these are not properly nested.
    return trace('fetch', (span, tracer, meter) => {
      span?.setAttributes({
        // dd legacy
        'resource.name': `${options.method} ${request}`,
        // otel
        'http.request.method': `${options.method}`,
        'http.route': `${request}`,
      })

      // Track retry attempts using retryDelay function
      // retryDelay is called after each failed request by ofetch
      // We always provide a retryDelay function to track metrics, but only override
      // the delay behavior if one was explicitly provided
      // Feature flag: NUXT_HTTP_V2_ENABLE_RETRY_COUNTER (defaults to false/disabled)
      const isRetryCounterEnabled =
        (process.env.NUXT_HTTP_V2_ENABLE_RETRY_COUNTER ?? 'false') === 'true'
      const originalRetryDelay = options.retryDelay
      const retryCounter = meter?.createCounter('bm.http.fetch.retry_attempt', {
        description: 'Number of HTTP fetch retry attempts',
      })
      const retryDelayWithMetrics = (context: FetchContext): number => {
        if (isRetryCounterEnabled && tracer && meter) {
          const retryReason = getRetryReason(context)

          retryCounter?.add(1, {
            'http.request.method': `${options.method}`,
            'http.route': `${request}`,
            'bm.retry.reason': retryReason,
          })
        }

        // If a retryDelay was provided, use it; otherwise return 0
        // (ofetch's default retryDelay is 0, so we're not changing the behavior)
        if (typeof originalRetryDelay === 'function') {
          return originalRetryDelay(context)
        }
        if (typeof originalRetryDelay === 'number') {
          return originalRetryDelay
        }

        // Return 0 to match ofetch's default retry delay behavior
        return 0
      }

      const listeners: Parameters<typeof $fetch>[1] = {
        // Instrumentation notes:
        // If `onRequestError` is called in ofetch
        // then `onResponse` will not be called.
        // Regardless of response status (success, failure)
        // `onResponse` will be called so no need
        // to call span.end in `onResponseError`.
        async onRequestError(context) {
          span?.end()

          await callHooks(context, options.onRequestError)
        },
        async onResponse(context) {
          span?.end()

          await callHooks(context, options.onResponse)
        },
        async onResponseError(context) {
          await callHooks(context, options.onResponseError)
        },
        retryDelay: retryDelayWithMetrics,
      }

      return callFetch(resolvedPath, merge({}, options, listeners))
    })
  }

  /**
   * Create a new `$httpFetch` instance from another `$httpFetch` instance,
   * inheriting the global options used to build the first one.
   *
   * Note that the object returned from the original `getDefaultHeaders` option
   * gets merged with the override and is not simply overridden as the other
   * options. This allows consumers to augment an `$httpFetch` instance with
   * missing headers for example.
   */
  $httpFetch.create = (globalOverrides: Partial<CreateHttpFetchOptions>) => {
    return createHttpFetch({
      ...globalOptions,
      ...globalOverrides,

      // This option is not automatically overridden because we want both the
      // parent and the override functions to be called, and then merge their
      // results together.
      getDefaultHeaders: (path?: string) => {
        const parentHeaders = globalOptions?.getDefaultHeaders?.(path)
        const headers = globalOverrides.getDefaultHeaders?.(path)

        return {
          ...parentHeaders,
          ...headers,
        }
      },
    })
  }

  function $httpFetchTryCatch<
    Path extends keyof CombinedApiSpecs & string,
    Method extends MethodType,
  >(
    request: Path,
    requestOptions?: CombinedApiSpecs[Path] extends Record<
      Method,
      EndpointDefinition
    >
      ? HttpFetchOptions<
          Method,
          Path,
          CombinedApiSpecs[Path][Method] extends EndpointDefinition<
            any,
            infer RequestOptions
          >
            ? FilterOutUndefinedOptions<Omit<RequestOptions, 'response'>>
            : never
        >
      : never,
  ): Promise<
    SafeResult<EndpointResponse<CombinedApiSpecs, Path, Method>, FetchError>
  > {
    return tryCatch(() => $httpFetch(request, requestOptions))
  }

  $httpFetch.tryCatch = $httpFetchTryCatch

  return $httpFetch as HttpFetch
}
